import {
  Button,
  CardContent,
  Grid,
  TextField,
  Typography,
  Divider,
  Modal,
  Box,
  IconButton,
} from "@mui/material";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../../../utils/Url";
import ToastApp from "../../../ToastApp";
import CloseIcon from "@mui/icons-material/Close";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  maxWidth: '90%',
  maxHeight: '90vh',
  overflow: 'auto',
};

type Props = {};

const CreateNotice = (props: Props) => {
  const [toaster, setToaster] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'image' | 'pdf' | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const validationSchema = Yup.object({
    notice: Yup.string()
      .required(t("text.noticeTitleRequired"))
      .test(
        'trim',
        t("text.noticeTitleRequired"),
        function (value) {
          return !!(value && value.trim() !== '');
        }
      ),
    description: Yup.string()
      .required(t("text.descriptionRequired"))
      .test(
        'trim',
        t("text.descriptionRequired"),
        function (value) {
          return !!(value && value.trim() !== '');
        }
      ),
    fromDate: Yup.date()
      .required(t("text.fromDateRequired"))
      .nullable(),
    toDate: Yup.date()
      .required(t("text.toDateRequired"))
      .nullable()
      .min(Yup.ref('fromDate'), t("text.toDateAfterFromDate")),
    noticeDoc: Yup.mixed()
      .test('fileSize', t("text.fileTooLarge"), (value) => {
        if (value && value instanceof File) {
          return value.size <= 5000000; // 5MB
        }
        return true;
      })
      .test('fileType', t("text.unsupportedFormat"), (value) => {
        if (value && value instanceof File) {
          return ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'].includes(value.type);
        }
        return true;
      })
  });

  let userId: any = localStorage.getItem("useR_ID");
  if (userId !== null) {
    userId = userId.replace(/\D/g, '');
  } else {
    toast.error("User ID not Found");
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);

      if (file.type === 'application/pdf') {
        setFileType('pdf');
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          formik.setFieldValue("noticeDoc", base64String);
          setFilePreview(base64String);
        };
        reader.readAsDataURL(file);
      } else {
        setFileType('image');
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          formik.setFieldValue("noticeDoc", base64String);
          setFilePreview(base64String);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handlePreviewOpen = () => {
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      noticeID: 0,
      notice: "",
      description: "",
      fromDate: "",
      toDate: "",
      noticeLink: "",
      isActive: true,
      sortOrder: 0,
      createdDt: new Date().toISOString(),
      modifyDt: new Date().toISOString(),
      user_ID: parseInt(userId),
      noticeDoc: "",
      noticeDate: new Date().toISOString()
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const formattedValues = {
          ...values,
          fromDate: values.fromDate ? new Date(values.fromDate).toISOString() : null,
          toDate: values.toDate ? new Date(values.toDate).toISOString() : null,
          noticeDate: new Date().toISOString()
        };

        const response = await api.post(
          `Notice/AddUpdateNoticeMaster`,
          formattedValues
        );

        if (response.data.isSuccess) {
          toast.success(response.data.mesg);
          setToaster(false);
          navigate('/newsLetter/NoticeMaster');
        } else {
          setToaster(true);
          toast.error(response.data.mesg);
        }
      } catch (error) {
        setToaster(true);
        toast.error("An error occurred while creating the notice");
      }
    },
  });

  const requiredFields = ['notice', 'description', 'fromDate', 'toDate'];

  const back = useNavigate();
  return (
    <div>
      <div
        style={{
          padding: "5px",
          backgroundColor: "#ffffff",
          borderRadius: "5px",
          marginTop: "5px",
          border: ".5px solid #FF7722",
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            textAlign="center"
            style={{ fontSize: "18px", fontWeight: 500 }}
          >
            {t("text.CreateNotice")}
          </Typography>

          <Grid item sm={4} xs={12}>
            <Typography style={{ marginTop: "-75px" }}>
              <Button
                type="submit"
                onClick={() => back(-1)}
                variant="contained"
                style={{
                  marginBottom: 15,
                  marginTop: "45px",
                  backgroundColor: "blue",
                  width: 20,
                }}
              >
                <ArrowBackSharpIcon />
              </Button>
            </Typography>
          </Grid>
          <Divider />
          <br />
          <form onSubmit={formik.handleSubmit}>
            {toaster === false ? "" : <ToastApp />}
            <Grid container spacing={2}>
              <Grid item lg={6} sm={6} xs={12}>
                <TextField
                  label={
                    <span>
                      {t("text.NoticeTitle")}
                      {requiredFields.includes('notice') && (
                        <span style={{ color: formik.values.notice ? 'green' : 'red' }}>*</span>
                      )}
                    </span>
                  }
                  value={formik.values.notice}
                  name="notice"
                  id="notice"
                  placeholder={t("text.EnterNoticeTitle")}
                  size="small"
                  fullWidth
                  style={{ backgroundColor: "white" }}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.notice && formik.errors.notice ? (
                  <div style={{ color: "red", margin: "5px" }}>
                    {formik.errors.notice}
                  </div>
                ) : null}
              </Grid>

              <Grid item lg={6} sm={6} xs={12}>
                <TextField
                  label={t("text.NoticeLink")}
                  value={formik.values.noticeLink}
                  name="noticeLink"
                  id="noticeLink"
                  placeholder={t("text.EnterNoticeLink")}
                  size="small"
                  fullWidth
                  style={{ backgroundColor: "white" }}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.noticeLink && formik.errors.noticeLink ? (
                  <div style={{ color: "red", margin: "5px" }}>
                    {formik.errors.noticeLink}
                  </div>
                ) : null}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={
                    <span>
                      {t("text.Description")}
                      {requiredFields.includes('description') && (
                        <span style={{ color: formik.values.description ? 'green' : 'red' }}>*</span>
                      )}
                    </span>
                  }
                  value={formik.values.description}
                  name="description"
                  id="description"
                  placeholder={t("text.EnterDescription")}
                  size="small"
                  fullWidth
                  multiline
                  rows={4}
                  style={{ backgroundColor: "white" }}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.description && formik.errors.description ? (
                  <div style={{ color: "red", margin: "5px" }}>
                    {formik.errors.description}
                  </div>
                ) : null}
              </Grid>

              <Grid item lg={6} sm={6} xs={12}>
                <TextField
                  label={
                    <span>
                      {t("text.FromDate")}
                      {requiredFields.includes('fromDate') && (
                        <span style={{ color: formik.values.fromDate ? 'green' : 'red' }}>*</span>
                      )}
                    </span>
                  }
                  type="date"
                  value={formik.values.fromDate}
                  name="fromDate"
                  id="fromDate"
                  size="small"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ backgroundColor: "white" }}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.fromDate && Boolean(formik.errors.fromDate)}
                  helperText={formik.touched.fromDate && formik.errors.fromDate}
                />
              </Grid>

              <Grid item lg={6} sm={6} xs={12}>
                <TextField
                  label={
                    <span>
                      {t("text.ToDate")}
                      {requiredFields.includes('toDate') && (
                        <span style={{ color: formik.values.toDate ? 'green' : 'red' }}>*</span>
                      )}
                    </span>
                  }
                  type="date"
                  value={formik.values.toDate}
                  name="toDate"
                  id="toDate"
                  size="small"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ backgroundColor: "white" }}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.toDate && Boolean(formik.errors.toDate)}
                  helperText={formik.touched.toDate && formik.errors.toDate}
                  inputProps={{
                    min: formik.values.fromDate
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <input
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="notice-doc-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="notice-doc-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={selectedFile?.type === 'application/pdf' ? <PictureAsPdfIcon /> : <InsertPhotoIcon />}
                    style={{ marginRight: '10px' }}
                  >
                    {t("text.UploadFile")}
                  </Button>
                </label>
                {selectedFile && (
                  <>
                    <span style={{ marginRight: '10px' }}>{selectedFile.name}</span>
                    {filePreview && (
                      <Button
                        variant="outlined"
                        onClick={handlePreviewOpen}
                      >
                        {t("text.PreviewFile")}
                      </Button>
                    )}
                  </>
                )}
                {formik.touched.noticeDoc && formik.errors.noticeDoc ? (
                  <div style={{ color: "red", margin: "5px" }}>
                    {formik.errors.noticeDoc}
                  </div>
                ) : null}
              </Grid>

              <Modal
                open={previewOpen}
                onClose={handlePreviewClose}
                aria-labelledby="file-preview-modal"
                aria-describedby="file-preview-description"
              >
                <Box sx={style}>
                  <IconButton
                    aria-label="close"
                    onClick={handlePreviewClose}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  {fileType === 'image' && filePreview && (
                    <img
                      src={filePreview}
                      alt="Notice Preview"
                      style={{ maxWidth: '100%', maxHeight: '80vh' }}
                    />
                  )}
                  {fileType === 'pdf' && filePreview && (
                    <embed
                      src={filePreview}
                      type="application/pdf"
                      width="100%"
                      height="600px"
                    />
                  )}
                </Box>
              </Modal>

              <Grid item xs={12} container spacing={2} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Grid item lg={6} sm={6} xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    style={{
                      backgroundColor: "#059669",
                      color: "white",
                      marginTop: "10px",
                    }}
                  >
                    {t("text.save")}
                  </Button>
                </Grid>

                <Grid item lg={6} sm={6} xs={12}>
                  <Button
                    type="reset"
                    fullWidth
                    style={{
                      backgroundColor: "#F43F5E",
                      color: "white",
                      marginTop: "10px",
                    }}
                    onClick={() => {
                      formik.resetForm();
                      setSelectedFile(null);
                      setFilePreview(null);
                      setFileType(null);
                    }}
                  >
                    {t("text.reset")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </div>
    </div>
  );
};

export default CreateNotice;

























// import {
//   Button,
//   CardContent,
//   Grid,
//   TextField,
//   Typography,
//   Divider,
//   Modal,
//   Box,
//   IconButton,
// } from "@mui/material";
// import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
// import { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { useTranslation } from "react-i18next";
// import { useFormik } from "formik";
// import * as Yup from "yup";
// import { toast } from "react-toastify";
// import api from "../../../utils/Url";
// import ToastApp from "../../../ToastApp";
// import CloseIcon from "@mui/icons-material/Close";
// import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";

// const style = {
//   position: 'absolute' as 'absolute',
//   top: '50%',
//   left: '50%',
//   transform: 'translate(-50%, -50%)',
//   bgcolor: 'background.paper',
//   boxShadow: 24,
//   p: 4,
//   maxWidth: '90%',
//   maxHeight: '90vh',
//   overflow: 'auto',
// };

// type Props = {};

// const CreateNotice = (props: Props) => {
//   const [toaster, setToaster] = useState(false);
//   const [previewOpen, setPreviewOpen] = useState(false);
//   const [selectedFile, setSelectedFile] = useState<File | null>(null);
//   const [imagePreview, setImagePreview] = useState<string | null>(null);
//   const navigate = useNavigate();
//   const { t } = useTranslation();

//   const validationSchema = Yup.object({
//     notice: Yup.string()
//       .required(t("text.noticeTitleRequired"))
//       .test(
//         'trim',
//         t("text.noticeTitleRequired"),
//         function (value) {
//           return !!(value && value.trim() !== '');
//         }
//       ),
//     description: Yup.string()
//       .required(t("text.descriptionRequired"))
//       .test(
//         'trim',
//         t("text.descriptionRequired"),
//         function (value) {
//           return !!(value && value.trim() !== '');
//         }
//       ),
//     fromDate: Yup.date()
//       .required(t("text.fromDateRequired"))
//       .nullable(),
//     toDate: Yup.date()
//       .required(t("text.toDateRequired"))
//       .nullable()
//       .min(Yup.ref('fromDate'), t("text.toDateAfterFromDate")),
//     // noticeLink: Yup.string()
//     //   .url(t("text.validUrl"))
//     //   .nullable(),
//     noticeDoc: Yup.mixed()
//       .test('fileSize', t("text.fileTooLarge"), (value) => {
//         if (value && value instanceof File) {
//           return value.size <= 5000000; // 5MB
//         }
//         return true;
//       })
//       .test('fileType', t("text.unsupportedFormat"), (value) => {
//         if (value && value instanceof File) {
//           return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
//         }
//         return true;
//       })
//   });

//   let userId: any = localStorage.getItem("useR_ID");
//   if (userId !== null) {
//     userId = userId.replace(/\D/g, '');
//   } else {
//     toast.error("User ID not Found");
//   }

//   const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     if (event.target.files && event.target.files[0]) {
//       const file = event.target.files[0];
//       setSelectedFile(file);

//       const reader = new FileReader();
//       reader.onloadend = () => {
//         const base64String = reader.result as string;
//         formik.setFieldValue("noticeDoc", base64String);
//         setImagePreview(base64String);
//       };
//       reader.readAsDataURL(file);
//     }
//   };

//   const handlePreviewOpen = () => {
//     setPreviewOpen(true);
//   };

//   const handlePreviewClose = () => {
//     setPreviewOpen(false);
//   };

//   const formik = useFormik({
//     initialValues: {
//       noticeID: 0,
//       notice: "",
//       description: "",
//       fromDate: "",
//       toDate: "",
//       noticeLink: "",
//       isActive: true,
//       sortOrder: 0,
//       createdDt: new Date().toISOString(),
//       modifyDt: new Date().toISOString(),
//       user_ID: parseInt(userId),
//       noticeDoc: "",
//       noticeDate: new Date().toISOString()
//     },
//     validationSchema: validationSchema,
//     onSubmit: async (values) => {
//       try {
//         const formattedValues = {
//           ...values,
//           fromDate: values.fromDate ? new Date(values.fromDate).toISOString() : null,
//           toDate: values.toDate ? new Date(values.toDate).toISOString() : null,
//           noticeDate: new Date().toISOString()
//         };

//         const response = await api.post(
//           `Notice/AddUpdateNoticeMaster`,
//           formattedValues
//         );

//         if (response.data.isSuccess) {
//           toast.success(response.data.mesg);
//           setToaster(false);
//           navigate('/newsLetter/NoticeMaster');
//         } else {
//           setToaster(true);
//           toast.error(response.data.mesg);
//         }
//       } catch (error) {
//         setToaster(true);
//         toast.error("An error occurred while creating the notice");
//       }
//     },
//   });

//   const requiredFields = ['notice', 'description', 'fromDate', 'toDate'];

//   const back = useNavigate();
//   return (
//     <div>
//       <div
//         style={{
//           padding: "5px",
//           backgroundColor: "#ffffff",
//           borderRadius: "5px",
//           marginTop: "5px",
//           border: ".5px solid #FF7722",
//         }}
//       >
//         <CardContent>
//           <Typography
//             variant="h5"
//             textAlign="center"
//             style={{ fontSize: "18px", fontWeight: 500 }}
//           >
//             {t("text.CreateNotice")}
//           </Typography>

//           <Grid item sm={4} xs={12}>
//             <Typography style={{ marginTop: "-75px" }}>
//               <Button
//                 type="submit"
//                 onClick={() => back(-1)}
//                 variant="contained"
//                 style={{
//                   marginBottom: 15,
//                   marginTop: "45px",
//                   backgroundColor: "blue",
//                   width: 20,
//                 }}
//               >
//                 <ArrowBackSharpIcon />
//               </Button>
//             </Typography>
//           </Grid>
//           <Divider />
//           <br />
//           <form onSubmit={formik.handleSubmit}>
//             {toaster === false ? "" : <ToastApp />}
//             <Grid container spacing={2}>
//               <Grid item lg={6} sm={6} xs={12}>
//                 <TextField
//                   label={
//                     <span>
//                       {t("text.NoticeTitle")}
//                       {requiredFields.includes('notice') && (
//                         <span style={{ color: formik.values.notice ? 'green' : 'red' }}>*</span>
//                       )}
//                     </span>
//                   }
//                   value={formik.values.notice}
//                   name="notice"
//                   id="notice"
//                   placeholder={t("text.EnterNoticeTitle")}
//                   size="small"
//                   fullWidth
//                   style={{ backgroundColor: "white" }}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                 />
//                 {formik.touched.notice && formik.errors.notice ? (
//                   <div style={{ color: "red", margin: "5px" }}>
//                     {formik.errors.notice}
//                   </div>
//                 ) : null}
//               </Grid>

//               <Grid item lg={6} sm={6} xs={12}>
//                 <TextField
//                   label={t("text.NoticeLink")}
//                   value={formik.values.noticeLink}
//                   name="noticeLink"
//                   id="noticeLink"
//                   placeholder={t("text.EnterNoticeLink")}
//                   size="small"
//                   fullWidth
//                   style={{ backgroundColor: "white" }}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                 />
//                 {formik.touched.noticeLink && formik.errors.noticeLink ? (
//                   <div style={{ color: "red", margin: "5px" }}>
//                     {formik.errors.noticeLink}
//                   </div>
//                 ) : null}
//               </Grid>

//               <Grid item xs={12}>
//                 <TextField
//                   label={
//                     <span>
//                       {t("text.Description")}
//                       {requiredFields.includes('description') && (
//                         <span style={{ color: formik.values.description ? 'green' : 'red' }}>*</span>
//                       )}
//                     </span>
//                   }
//                   value={formik.values.description}
//                   name="description"
//                   id="description"
//                   placeholder={t("text.EnterDescription")}
//                   size="small"
//                   fullWidth
//                   multiline
//                   rows={4}
//                   style={{ backgroundColor: "white" }}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                 />
//                 {formik.touched.description && formik.errors.description ? (
//                   <div style={{ color: "red", margin: "5px" }}>
//                     {formik.errors.description}
//                   </div>
//                 ) : null}
//               </Grid>

//               <Grid item lg={6} sm={6} xs={12}>
//                 <TextField
//                   label={
//                     <span>
//                       {t("text.FromDate")}
//                       {requiredFields.includes('fromDate') && (
//                         <span style={{ color: formik.values.fromDate ? 'green' : 'red' }}>*</span>
//                       )}
//                     </span>
//                   }
//                   type="date"
//                   value={formik.values.fromDate}
//                   name="fromDate"
//                   id="fromDate"
//                   size="small"
//                   fullWidth
//                   InputLabelProps={{
//                     shrink: true,
//                   }}
//                   style={{ backgroundColor: "white" }}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                   error={formik.touched.fromDate && Boolean(formik.errors.fromDate)}
//                   helperText={formik.touched.fromDate && formik.errors.fromDate}
//                 />
//               </Grid>

//               <Grid item lg={6} sm={6} xs={12}>
//                 <TextField
//                   label={
//                     <span>
//                       {t("text.ToDate")}
//                       {requiredFields.includes('toDate') && (
//                         <span style={{ color: formik.values.toDate ? 'green' : 'red' }}>*</span>
//                       )}
//                     </span>
//                   }
//                   type="date"
//                   value={formik.values.toDate}
//                   name="toDate"
//                   id="toDate"
//                   size="small"
//                   fullWidth
//                   InputLabelProps={{
//                     shrink: true,
//                   }}
//                   style={{ backgroundColor: "white" }}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                   error={formik.touched.toDate && Boolean(formik.errors.toDate)}
//                   helperText={formik.touched.toDate && formik.errors.toDate}
//                   inputProps={{
//                     min: formik.values.fromDate
//                   }}
//                 />
//               </Grid>

//               <Grid item xs={12}>
//                 <input
//                   accept="image/*"
//                   style={{ display: 'none' }}
//                   id="notice-doc-upload"
//                   type="file"
//                   onChange={handleFileChange}
//                 />
//                 <label htmlFor="notice-doc-upload">
//                   <Button
//                     variant="contained"
//                     component="span"
//                     startIcon={<InsertPhotoIcon />}
//                     style={{ marginRight: '10px' }}
//                   >
//                     {t("text.UploadImage")}
//                   </Button>
//                 </label>
//                 {selectedFile && (
//                   <>
//                     <span style={{ marginRight: '10px' }}>{selectedFile.name}</span>
//                     {imagePreview && (
//                       <Button
//                         variant="outlined"
//                         onClick={handlePreviewOpen}
//                       >
//                         {t("text.PreviewImage")}
//                       </Button>
//                     )}
//                   </>
//                 )}
//                 {formik.touched.noticeDoc && formik.errors.noticeDoc ? (
//                   <div style={{ color: "red", margin: "5px" }}>
//                     {formik.errors.noticeDoc}
//                   </div>
//                 ) : null}
//               </Grid>

//               <Modal
//                 open={previewOpen}
//                 onClose={handlePreviewClose}
//                 aria-labelledby="image-preview-modal"
//                 aria-describedby="image-preview-description"
//               >
//                 <Box sx={style}>
//                   <IconButton
//                     aria-label="close"
//                     onClick={handlePreviewClose}
//                     sx={{
//                       position: 'absolute',
//                       right: 8,
//                       top: 8,
//                       color: (theme) => theme.palette.grey[500],
//                     }}
//                   >
//                     <CloseIcon />
//                   </IconButton>
//                   {imagePreview && (
//                     <img
//                       src={imagePreview}
//                       alt="Notice Preview"
//                       style={{ maxWidth: '100%', maxHeight: '80vh' }}
//                     />
//                   )}
//                 </Box>
//               </Modal>

//               <Grid item xs={12} container spacing={2} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//                 <Grid item lg={6} sm={6} xs={12}>
//                   <Button
//                     type="submit"
//                     fullWidth
//                     style={{
//                       backgroundColor: "#059669",
//                       color: "white",
//                       marginTop: "10px",
//                     }}
//                   >
//                     {t("text.save")}
//                   </Button>
//                 </Grid>

//                 <Grid item lg={6} sm={6} xs={12}>
//                   <Button
//                     type="reset"
//                     fullWidth
//                     style={{
//                       backgroundColor: "#F43F5E",
//                       color: "white",
//                       marginTop: "10px",
//                     }}
//                     onClick={() => {
//                       formik.resetForm();
//                       setSelectedFile(null);
//                       setImagePreview(null);
//                     }}
//                   >
//                     {t("text.reset")}
//                   </Button>
//                 </Grid>
//               </Grid>
//             </Grid>
//           </form>
//         </CardContent>
//       </div>
//     </div>
//   );
// };

// export default CreateNotice;